<!--
Copyright (c) 2023-2026, NVIDIA CORPORATION.  All rights reserved.

NVIDIA CORPORATION and its licensors retain all intellectual property
and proprietary rights in and to this software, related documentation
and any modifications thereto.  Any use, reproduction, disclosure or
distribution of this software and related documentation without an express
license agreement from NVIDIA CORPORATION is strictly prohibited.
-->

<div
    class="fullpage-dialog"
    nvHigBackgroundImageOverlay
    [imageUrl]="data?.backgroundImageUrl"
    fxFlex
    fxLayout="column"
    nvTrapFocus
>
    <span class="header font-sub1">{{ data.header.text | translate }}</span>
    <span class="title font-header4" role="heading" aria-level="2">{{ data.title.text | translate }}</span>
    @if (data?.subtitle) {
        <span class="subtitle font-body2">{{ data.subtitle.text | translate: data.subtitle?.placeholders }}</span>
    }
    @if (data.metadata?.showMetadata) {
        <nv-hig-list-metadata class="hig-dialog-metadata" [metadata]="data.metadata"> </nv-hig-list-metadata>
    }
    @if (data.dataTableColumns?.length > 0) {
        <nv-hig-table-comparison
            class="hig-dialog-table"
            [ariaLabel]="'layout.storageUpsellDialog.ariaLabel' | translate"
            [columns]="data.dataTableColumns"
            (columnActionClick)="onTableCTAClick($event)"
        >
        </nv-hig-table-comparison>
    }
    @if (data.checkbox) {
        <div class="hig-dialog-checkbox">
            <mat-checkbox [(ngModel)]="data.checkboxState" class="nv-hig">
                <span class="font-body2">{{ data.checkbox.text | translate }}</span>
            </mat-checkbox>
        </div>
    }
    @if (choiceChip) {
        <div fxLayout="column" class="choice-chip-container">
            <div class="choice-chip font-sub2">
                @if (choiceChip.choiceChipIcon?.text) {
                    <img alt="" class="chip-icon" [src]="choiceChip.choiceChipIcon.text" />
                }
                <span>{{
                    choiceChip.choiceChipTitle.translated
                        ? choiceChip.choiceChipTitle.text
                        : (choiceChip.choiceChipTitle.text | translate)
                }}</span>
            </div>
            @if (choiceChip.choiceChipContent) {
                <div fxLayout="row" class="steps">
                    @for (step of choiceChip.choiceChipContent; track step) {
                        <div fxLayout="column" class="step">
                            <div class="icon-container">
                                @if (step?.stepImage?.url; as stepImageUrl) {
                                    <qrcode
                                        role="img"
                                        [attr.aria-label]="'common.qrCode' | translate"
                                        [qrdata]="stepImageUrl"
                                        [width]="qrCodeWidth"
                                        [errorCorrectionLevel]="'M'"
                                    ></qrcode>
                                }
                                @if (step?.stepImage?.matIcon; as stepImageMatIcon) {
                                    <mat-icon class="icon font-body2" [svgIcon]="stepImageMatIcon"></mat-icon>
                                }
                            </div>
                            <span class="step-title font-body2">{{ step.stepTitle.text | translate }}</span>
                            <span class="step-description font-body3">{{ step.stepDescription.text | translate }}</span>
                        </div>
                    }
                </div>
            }
            <div class="buttons" nvAutoFocus autoFocusPolicy="initial">
                @for (button of choiceChip?.choiceChipButtons; track button) {
                    <button
                        mat-button
                        class="font-button2"
                        (click)="onButtonClick(button)"
                        [disabled]="button?.isDisabled"
                    >
                        {{ button.text | translate }}
                    </button>
                }
            </div>
            @if (choiceChip?.choiceChipStatusText?.title) {
                <span class="status-text font-sub3">{{ choiceChip.choiceChipStatusText.title.text | translate }}</span>
            }
        </div>
    } @else {
        <div nvFast1DSpatialNavigation [isRTL]="isRTL" fxLayout="row" class="selector-cards">
            @for (cardItem of selectorCards; track cardItem; let i = $index) {
                <div
                    class="selector-card"
                    [class]="
                        i === expandedSelectorCardIndex
                            ? 'expanded'
                            : i > expandedSelectorCardIndex
                              ? 'next-card'
                              : 'previous-card'
                    "
                    (focus)="expandSelectorCard(i)"
                    (mousemove)="expandSelectorCard(i)"
                    nvFastSpatialNavigationItem
                    tabindex="0"
                    #selectorCardElements
                >
                    <div class="card-icon">
                        <!-- Using visibility to preserve the icon space so that the text remains at the same position regardless of the icon -->
                        <img
                            alt=""
                            class="icon"
                            [ngStyle]="{ visibility: cardItem.selectorCardIcon?.url ? 'visible' : 'hidden' }"
                            [src]="cardItem.selectorCardIcon.url"
                        />
                        <span class="font-body3">{{ cardItem.selectorCardIcon.caption }}</span>
                    </div>
                    <div class="card-text">
                        <div class="selector-card-secondary-info">
                            @if (cardItem?.secondaryInfo?.text) {
                                <span class="font-sub3 selector-card-secondary-info-text">{{
                                    cardItem.secondaryInfo.text | translate
                                }}</span>
                            }
                            @if (cardItem?.secondaryInfo?.matIcon) {
                                <mat-icon
                                    class="selector-card-secondary-info-icon icon font-body2"
                                    [svgIcon]="cardItem.secondaryInfo.matIcon"
                                ></mat-icon>
                            }
                        </div>
                        @if (cardItem?.selectorCardStatusText) {
                            <span class="card-status-text font-body3">{{
                                cardItem.selectorCardStatusText.text | translate
                            }}</span>
                        }
                    </div>
                    @if (cardItem?.selectorCardButtons) {
                        <div class="card-buttons">
                            @for (button of cardItem.selectorCardButtons; track button) {
                                <button
                                    mat-button
                                    class="font-button2 card-button"
                                    (click)="onButtonClick(button)"
                                    [disabled]="button?.isDisabled"
                                    [attr.aria-label]="
                                        'fullpageDialog.buttonWithStore'
                                            | translate
                                                : {
                                                      button: button.text | translate,
                                                      store: cardItem.selectorCardIcon.caption
                                                  }
                                    "
                                >
                                    {{ button.text | translate }}
                                </button>
                            }
                        </div>
                    }
                </div>
            }
        </div>
    }
    <!-- close button in top-right corner -->
    @if (data.closeButtonVisible) {
        <div class="hig-dialog-close-button">
            <button
                #closeButton
                mat-button
                (click)="onCloseClick()"
                class="close-button hig-button-icon"
                [disableRipple]="true"
                [attr.aria-label]="'common.close' | translate"
            >
                <mat-icon svgIcon="close"></mat-icon>
            </button>
        </div>
    }
</div>
